import AddActionButton from '@/components/AddActionButton';
import DetailsCard from '@/components/DetailsCard';
import { SortableItemContainer } from '@/components/DragAndDrop';
import KeyResultItem from '@/components/ProjectDetail/KeyResultItem';
import ManageKeyResultModal from '@/components/ProjectDetail/ManageKeyResultModal';
import { IconMountainTop } from '@/theme/icons';
import { KeyResult } from '@/types/project';
import rem from '@/utils/rem';
import { Box, Flex, Text, useDisclosure } from '@chakra-ui/react';
import { SortableContext } from '@dnd-kit/sortable';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  color: string;
  gradientColor: string;
  keyResults: KeyResult[];
  projectId: string;
  isArchived: boolean;
  onCreateAction: (name: string) => void;
  onCreateBlock: (name: string) => void;
  isTransparent?: boolean;
};

function KeyResults({
  color,
  keyResults,
  projectId,
  isArchived,
  gradientColor,
  onCreateAction,
  onCreateBlock,
  isTransparent,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    isOpen: isCreateKeyResultOpen,
    onOpen: onCreateKeyResultOpen,
    onClose: onCreateKeyResultClose,
  } = useDisclosure({ id: 'create' });
  const { t } = useTranslation('ProjectDetailComponents');

  const onOpen = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  // If transparent, render without DetailsCard wrapper
  if (isTransparent) {
    return (
      <Box width="full" paddingX={rem(16)} paddingY={rem(16)}>
        <Flex alignItems="center" justifyContent="space-between" marginBottom={rem(16)}>
          <Text color={color} fontSize="sm" fontWeight="700" letterSpacing="0.5px" textTransform="uppercase">
            {t('keyResult.sectionTitle')}
          </Text>
          <AddActionButton
            tooltipText={t('keyResult.addTooltip')}
            onClick={onCreateKeyResultOpen}
            variant="secondary"
            width={rem(28)}
            height={rem(28)}
            isDisabled={isArchived}
          />
        </Flex>

        {keyResults.length ? (
          <Box width="full">
            <SortableContext items={keyResults} disabled={isArchived || isOpen}>
              {keyResults.map((keyResult, index) => (
                <Box key={keyResult.id}>
                  <SortableItemContainer
                    id={keyResult.id}
                    payload={{ containerId: 'keyResult', category: null, index }}
                  >
                    <KeyResultItem
                      isArchived={isArchived}
                      keyResult={keyResult}
                      projectCategoryColor={gradientColor}
                      projectId={projectId}
                      index={index + 1}
                      onOpen={onOpen}
                      onClose={onClose}
                      onCreateAction={onCreateAction}
                      onCreateBlock={onCreateBlock}
                      isTransparent={isTransparent}
                    />
                  </SortableItemContainer>
                  {/* Add separator line between items, but not after the last item - only when transparent */}
                  {isTransparent && index < keyResults.length - 1 && (
                    <Box
                      width="full"
                      height="1px"
                      background="linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 20%, rgba(255, 255, 255, 0.2) 80%, transparent 100%)"
                    />
                  )}
                </Box>
              ))}
            </SortableContext>
          </Box>
        ) : (
          <Flex
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap={rem(8)}
            width="full"
            height="full"
          >
            {isArchived ? (
              <Text textStyle="smallBlack" color="gray.400" textAlign="start">
                {t('keyResult.noResults')}
              </Text>
            ) : (
              <>
                <Text textStyle="smallBlack" color="text-tertiary" textTransform="uppercase">
                  {t('keyResult.manifestTitle')}
                </Text>
                <Text textStyle="medium" color="text-primary" textAlign="center">
                  {t('keyResult.manifestDesc')}
                </Text>
              </>
            )}
          </Flex>
        )}

        <ManageKeyResultModal
          count={keyResults.length}
          projectId={projectId}
          isOpen={isCreateKeyResultOpen}
          onClose={onCreateKeyResultClose}
        />
      </Box>
    );
  }

  return (
    <DetailsCard
      paddingX={rem(16)}
      paddingY={rem(16)}
      color={color}
      icon={IconMountainTop}
      sectionTitle={t('keyResult.sectionTitle')}
      rightButton={
        <AddActionButton
          tooltipText={t('keyResult.addTooltip')}
          onClick={onCreateKeyResultOpen}
          variant="secondary"
          width={rem(28)}
          height={rem(28)}
          isDisabled={isArchived}
        />
      }
      flex="1"
      minWidth={0}
    >
      {keyResults.length ? (
        <Box width="full" marginTop={rem(-8)}>
          <SortableContext items={keyResults} disabled={isArchived || isOpen}>
            {keyResults.map((keyResult, index) => (
              <Box key={keyResult.id}>
                <SortableItemContainer id={keyResult.id} payload={{ containerId: 'keyResult', category: null, index }}>
                  <KeyResultItem
                    isArchived={isArchived}
                    keyResult={keyResult}
                    projectCategoryColor={gradientColor}
                    projectId={projectId}
                    index={index + 1}
                    onOpen={onOpen}
                    onClose={onClose}
                    onCreateAction={onCreateAction}
                    onCreateBlock={onCreateBlock}
                    isTransparent={isTransparent}
                  />
                </SortableItemContainer>
                {/* Add separator line between items, but not after the last item - only when transparent */}
                {isTransparent && index < keyResults.length - 1 && (
                  <Box
                    width="full"
                    height="1px"
                    background="linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 20%, rgba(255, 255, 255, 0.2) 80%, transparent 100%)"
                  />
                )}
              </Box>
            ))}
          </SortableContext>
        </Box>
      ) : (
        <Flex
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gap={rem(8)}
          width="full"
          height="full"
        >
          {isArchived ? (
            <Text textStyle="smallBlack" color="gray.400" textAlign="start">
              {t('keyResult.noResults')}
            </Text>
          ) : (
            <>
              <Text textStyle="smallBlack" color="text-tertiary" textTransform="uppercase">
                {t('keyResult.manifestTitle')}
              </Text>
              <Text textStyle="medium" color="text-primary" textAlign="center">
                {t('keyResult.manifestDesc')}
              </Text>
            </>
          )}
        </Flex>
      )}
      <ManageKeyResultModal
        count={keyResults.length}
        projectId={projectId}
        isOpen={isCreateKeyResultOpen}
        onClose={onCreateKeyResultClose}
      />
    </DetailsCard>
  );
}

export default KeyResults;
